import React, { useState } from 'react';
import {
  Box,
  Badge,
  Alert,
  Tooltip,
  Menu,
  Typography,
  IconButton,
  Stack,
} from '@mui/material';
import { getTranslation } from 'common';
import { LabelOutlined, Title } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { CONSTANTS, SortCriterion } from '../common/utilities';
import { SortState } from '../hooks/useSortableList';
import { ProcessModelType } from '../model/processModel';
import { ServerTag, Task } from '../model/activitiesModel';
import { Priority } from '../hooks/usePriorities';
import MyActivitiesFilterMenu from './MyActivitiesFilterMenu';
import MyActivitiesSortMenu from './MyActivitiesSortMenu';
import MyActivitiesCardsViewMode from './MyActivitiesCardsViewMode';
import UserDataFilter from './UserDataFilter/UserDataFilter';
import { Organization } from 'model/organizationModel';
import { Translations } from 'model/messageModel';
import FlowentiButton from './flowenti-ui-kit/FlowentiButton';


interface MyActivitiesFuncBarProps {
  liveProcessCanUse: boolean | undefined;
  bypassSubscription: boolean | undefined;
  organization: Organization;
  translations: Translations;
  completeLabel: boolean;
  appliedFilter: number;
  sortCriteria: SortCriterion;
  setStartProcess: (start: boolean) => void;
  navigate: (path: string, options: { state: any }) => void;
  setCompleteLabel: (value: boolean | ((prev: boolean) => boolean)) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  searchTags: number[];
  setSearchTags: React.Dispatch<React.SetStateAction<number[]>>;
  selectedPriorities: string[];
  setSelectedPriorities: React.Dispatch<React.SetStateAction<string[]>>;
  selectedModels: number[];
  setSelectedModels: (models: number[]) => void;
  selectedActivities: string[];
  setSelectedActivities: (activities: string[]) => void;
  selectedUserDataKey: string | null;
  handleSelectUserDataKey: (key: string | null) => void;
  setSortCriteria: (criteria: any) => void;
  handleClearFilters: (disableLocalStorage?: boolean, clearUserData?: boolean) => void;
  processModels: ProcessModelType[];
  tasks: { todo: Task[], doing: Task[] };
  uniqueActivities: string[];
  displayedTags: ServerTag[];
  filteredTags: ServerTag[];
  showAllTags: boolean;
  setShowAllTags: React.Dispatch<React.SetStateAction<boolean>>;
  priorities: Priority[];
  userDataKeys: string[];
  SUPPORTED_SORTS: any[];
  view: 'minimal' | 'compact' | 'extended';
  handleViewChange: (newView: 'minimal' | 'compact' | 'extended') => void;
}

const MyActivitiesFuncBar: React.FC<MyActivitiesFuncBarProps> = ({
  liveProcessCanUse,
  bypassSubscription,
  organization,
  translations,
  completeLabel,
  appliedFilter,
  sortCriteria,
  setStartProcess,
  navigate,
  setCompleteLabel,
  searchText,
  setSearchText,
  searchTags,
  setSearchTags,
  selectedPriorities,
  setSelectedPriorities,
  selectedModels,
  setSelectedModels,
  selectedActivities,
  setSelectedActivities,
  selectedUserDataKey,
  handleSelectUserDataKey,
  setSortCriteria,
  handleClearFilters,
  processModels,
  tasks,
  uniqueActivities,
  displayedTags,
  filteredTags,
  showAllTags,
  setShowAllTags,
  priorities,
  userDataKeys,
  SUPPORTED_SORTS,
  view,
  handleViewChange,
}) => {
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [userDataMenuAnchorEl, setUserDataMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleUserDataClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserDataMenuAnchorEl(event.currentTarget);
  };

  const handleCloseUserData = () => {
    setUserDataMenuAnchorEl(null);
  };

  const handleCloseSort = () => {
    setSortAnchorEl(null);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleSortOptionClick = (field: string) => {
    setSortCriteria((prevState: any) => ({
      ...prevState,
      [field]:
        prevState[field] === SortState.Asc
          ? SortState.Desc
          : [SortState.None, undefined].includes(prevState[field])
            ? SortState.Asc
            : SortState.None,
    }));
  };
  return (
    <Stack>
      <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, mt: 3 }} id="utility-box">
        <FlowentiButton
          id="startprocess-button"
          size="small"
          variant="outlined"
          disabled={!bypassSubscription && !liveProcessCanUse}
          onClick={() => setStartProcess(true)}
        >
          {getTranslation(
            translations,
            "pm.startprocess.button.ok.caption"
          )}
        </FlowentiButton>
        {liveProcessCanUse === false && !bypassSubscription && (<Box display="flex" alignItems="center" gap={1}>
          <Alert id="live-process-exceeded" sx={{ height: 40, alignItems: "center", overflowY: "hidden" }} severity="error">{getTranslation(translations, "live_processes.limit.reached")}
          </Alert>
          <FlowentiButton
            variant="outlined"
            size="small"
            onClick={() => navigate(`/dashboard/organizations/${organization.id}/billing`, { state: { organizationName: organization.name } })}
          >
            {getTranslation(translations, "generic.upgrade")}
          </FlowentiButton>
        </Box>
        )}
        <Box id="view-and-filter-otpions" sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip title={getTranslation(translations, "labels_view.tooltip")}>
            <FlowentiButton
              size="small"
              variant="outlined"
              onClick={() => {
                setCompleteLabel((currentCompleteLabel: boolean) => {
                  localStorage.setItem(
                    CONSTANTS.STORE_LABEL_COMPLETE_MODE,
                    `${!currentCompleteLabel}`
                  );
                  return !currentCompleteLabel;
                });
              }}
              startIcon={completeLabel ? <Title color='primary' /> : <LabelOutlined color='primary' />}
            >
              {getTranslation(translations, "generic.labels.caption")}
            </FlowentiButton>
          </Tooltip>
          <MyActivitiesCardsViewMode
            view={view}
            handleViewChange={handleViewChange}
            translations={translations}
          />
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            color="primary"
            badgeContent={selectedUserDataKey ? 1 : 0}
          >
            <FlowentiButton
              id="userdata-menu"
              size="small"
              onClick={handleUserDataClick}
              variant="outlined"
              startIcon={<FormatListBulletedIcon color='primary' />}
            >
              {getTranslation(translations, "generic.userdata.caption")}
            </FlowentiButton>
          </Badge>
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            color="primary"
            badgeContent={appliedFilter}
          >
            <FlowentiButton
              id="filter-button"
              size="small"
              onClick={handleFilterClick}
              startIcon={<FilterListIcon color='primary' />} variant="outlined"
            >
              {getTranslation(translations, "generic.filters.caption")}
            </FlowentiButton>
          </Badge>
          <Badge
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            color="primary"
            badgeContent={
              Object.keys(sortCriteria).filter(
                (key) => sortCriteria[key] !== SortState.None
              ).length
            }
          >
            <FlowentiButton
              size="small"
              id="order-button"
              onClick={handleSortClick}
              startIcon={<SortIcon color='primary' />} variant="outlined"
            >
              {getTranslation(translations, "generic.sort.caption")}
            </FlowentiButton>
          </Badge>
        </Box>
      </Box>
      <MyActivitiesFilterMenu
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        translations={translations}
        searchText={searchText}
        setSearchText={setSearchText}
        handleClearFilters={handleClearFilters}
        processModels={processModels}
        selectedModels={selectedModels}
        setSelectedModels={setSelectedModels}
        tasks={tasks}
        uniqueActivities={uniqueActivities}
        selectedActivities={selectedActivities}
        setSelectedActivities={setSelectedActivities}
        displayedTags={displayedTags}
        searchTags={searchTags}
        setSearchTags={setSearchTags}
        filteredTags={filteredTags}
        showAllTags={showAllTags}
        setShowAllTags={setShowAllTags}
        priorities={priorities}
        selectedPriorities={selectedPriorities}
        setSelectedPriorities={setSelectedPriorities}
        userDataKeys={userDataKeys}
        selectedUserDataKey={selectedUserDataKey}
        handleSelectUserDataKey={handleSelectUserDataKey}
      />
      <MyActivitiesSortMenu
        open={Boolean(sortAnchorEl)}
        anchorEl={sortAnchorEl}
        onClose={handleCloseSort}
        sortCriteria={sortCriteria}
        SUPPORTED_SORTS={SUPPORTED_SORTS}
        selectedUserDataKey={selectedUserDataKey}
        handleSortOptionClick={handleSortOptionClick}
      />
      <Menu
        anchorEl={userDataMenuAnchorEl}
        open={Boolean(userDataMenuAnchorEl)}
        onClose={handleCloseUserData}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box p={2} component={Stack} sx={{ minWidth: 200, overflowX: "hidden" }}>
          {userDataKeys.length > 0 && (
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {getTranslation(translations, "generic.filter.by.userdata")}
              </Typography>
              <IconButton
                color="primary"
                onClick={() => {
                  handleSelectUserDataKey(null);
                  handleCloseUserData();
                }}
              >
                <FilterListOffIcon />
              </IconButton>
            </Box>
          )}
          <UserDataFilter
            availableKeys={userDataKeys}
            selectedKey={selectedUserDataKey}
            onSelectKey={handleSelectUserDataKey}
          />
        </Box>
      </Menu>
    </Stack>
  );
};

export default MyActivitiesFuncBar;